/**
 * Suggested replies, under the last message.
 *
 * They live on the board — `region:footer` holds a `ChipGroup` whose children
 * are `Button`s — and are DRAWN here, in the chat strip, because a suggested
 * reply is the visitor's next utterance rather than an artefact of the trip.
 * `region` is a hint; this is the layout answering it.
 *
 * Three rules, and the third has a wipe behind it:
 *
 * 1. **The value is sent VERBATIM.** `action.event.userMessage` IS the message,
 *    and on this wire that is DECLARED rather than agreed between two files.
 * 2. **Nothing is ever sent automatically** — no auto-tap, no pre-filling.
 * 3. **A chip whose message is EXACTLY the wipe confirmation is drawn as
 *    destructive.** Exact string comparison and nothing cleverer: the real
 *    reader is four conditions deep in the graph, and a second implementation
 *    of a consent belt in JavaScript is the class of bug this repo is full of,
 *    with a wiped profile as the price of the two drifting apart.
 *
 * Only the LAST block is tappable, and that is structural rather than policed:
 * the id `answers:turn` is fixed, so a new turn's chips REPLACE it.
 */

import { useMemo } from 'react';
import { useChat } from '../store/chatStore';
import { useSurface, REGION_IDS } from '../a2ui/surfaceStore';
import { readAction, dispatchTap, dispatchStarter } from '../a2ui/actions';
import { send } from '../lib/send';
import { cn } from '../lib/cn';
import { stripEmoji } from '../lib/emoji';
import { useSendLocked } from './turnLock';
import phrases from '../lib/generated/phrases.json';

type Chip =
  | { kind: 'tap'; id: string; label: string; message: string; action: Record<string, unknown> }
  | { kind: 'link'; id: string; label: string; url: string };

export default function Chips() {
  const components = useSurface((state) => state.components);
  const exiting = useSurface((state) => state.exiting);
  const surfaceId = useSurface((state) => state.surfaceId);
  const status = useChat((state) => state.status);

  const chips = useMemo<Chip[]>(() => {
    void exiting;
    const state = useSurface.getState();
    const out: Chip[] = [];
    for (const groupId of state.renderOrder(REGION_IDS.footer)) {
      const group = state.components[groupId];
      if (!group || group.component !== 'ChipGroup') continue;
      for (const childId of state.childrenOf(groupId)) {
        const node = state.components[childId];
        if (!node) continue;
        const props = node.props as Record<string, unknown>;
        const label = typeof props.label === 'string' ? props.label : '';
        if (!label) continue;
        // The label's own emoji — and the separate `emoji` prop — are not drawn. A
        // label that WAS only a picture still needs words: the line it sends.
        const text = stripEmoji(label);
        if (node.component === 'LinkButton' && typeof props.url === 'string') {
          out.push({ kind: 'link', id: childId, label: text || props.url, url: props.url });
          continue;
        }
        const tap = readAction(props.action, childId);
        if (tap) {
          out.push({
            kind: 'tap',
            id: childId,
            label: text || stripEmoji(tap.userMessage),
            message: tap.userMessage,
            action: tap.action,
          });
        }
      }
    }
    return out;
  }, [components, exiting]);

  if (chips.length === 0 || status !== 'idle') return null;

  return (
    <div className="chips">
      {chips.map((chip) => {
        if (chip.kind === 'link') {
          return (
            <a
              key={chip.id}
              className="chip chip--link"
              href={chip.url}
              target="_blank"
              rel="noreferrer noopener"
            >
              {chip.label}
            </a>
          );
        }
        const destructive = chip.message === phrases.forget.confirm_value;
        return (
          <button
            key={chip.id}
            type="button"
            className={cn('chip', destructive && 'chip--danger')}
            onClick={() =>
              dispatchTap({ userMessage: chip.message, action: chip.action }, chip.id, surfaceId)
            }
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * One opening suggestion tapped. A chip with a `starter` (the plan button, #142)
 * says which questionnaire it opens, and on `peterhof_agent` the graph opens it in
 * code — the button, not its words (`graphs/peterhof_plan/starter.py`). The line
 * itself is sent verbatim either way.
 */
export function tapStarter(chip: { value: string; starter?: string }): void {
  if (chip.starter) dispatchStarter(chip.value, chip.starter);
  else send(chip.value);
}

type StarterChip = { label: string; value: string; starter?: string };

/**
 * The opening suggestions, before the board has any of its own. A graph may
 * offer only some of them (`phrases.agent.starters`, #23) — the same entries.
 */
export function Starters({ chips = phrases.starters, disabled = false }: {
  chips?: readonly StarterChip[];
  disabled?: boolean;
}) {
  const sendLocked = useSendLocked();
  return (
    <div className="chips">
      {chips.map((chip) => (
        <button
          key={chip.value}
          type="button"
          className="chip"
          disabled={disabled || sendLocked}
          onClick={() => tapStarter(chip)}
        >
          {stripEmoji(chip.label)}
        </button>
      ))}
    </div>
  );
}
